import { ArrowLeft, GitMerge, List } from "@mynaui/icons-react";
import { Group, Shuffle } from "lucide-react";
import "./styles.scss";
import { Button, Loader, PageHeader, Spinner, useDialogs } from "../../ui";
import { useCreateSchedule } from "./useCreateSchedule";
import DependentStagesStep from "./components/DependentStagesStep";
import QualificationRulesStep from "./components/QualificationRulesStep";
import SchedulingTypeStep from "./components/SchedulingTypeStep";
import ManageGroupsStep from "./components/ManageGroupsStep";
import ManageBracketStep from "./components/ManageBracketStep";
import MatchFrequencyStep from "./components/MatchFrequencyStep";
import MatchSchedulingStep from "./components/MatchSchedulingStep";
import MexicanoSetupStep from "./components/MexicanoSetupStep";
import { isAmericanoFormat, isLiveRoundFormat } from "../../utils/methods";

const CreateSchedule = () => {
  const schedule = useCreateSchedule();
  const {
    loading, createLoading, currentStep, stage,
    navigate, searchParams, id, tournamentCategoryId, stageId,
    nextHandler, backHandler,
  } = schedule;
  const { confirm } = useDialogs();

  // Back is a no-op on the first step (and on Americano's first step, match-frequency)
  const isFirstStep =
    currentStep === "dependent-stages" ||
    (isAmericanoFormat(stage?.formatName) && currentStep === "match-frequency");

  return (
    <div className="create-schedule-container">
      {loading ? (
        <Loader minHeight={300} />
      ) : (
        <>
          <Button
            variant="link"
            className="back-btn-container"
            icon={<ArrowLeft size={18} />}
            onClick={async () => {
              if (!(await confirm("Are you sure you want to leave? All schedule configurations will be lost."))) return;
              const goTo = searchParams.get("goTo");
              if (goTo !== null)
                navigate(
                  goTo === "stage"
                    ? `/dashboard/tournaments/${id}/${tournamentCategoryId}/stages/${stageId}`
                    : `/dashboard/tournaments/${id}/${tournamentCategoryId}`
                );
              else navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}`);
            }}
          >
            Back
            {searchParams.get("goTo")
              ? searchParams.get("goTo") === "category"
                ? " to Category Detail"
                : " to Stage Overview"
              : ""}
          </Button>
          <PageHeader
            className="create-schedule-header"
            title={
              <span className="create-schedule-title">
                <span className="create-schedule-title__icon" aria-hidden="true">
                  {stage?.formatName === "Knockout" ? (
                    <GitMerge />
                  ) : stage?.formatName === "League" ? (
                    <List />
                  ) : isAmericanoFormat(stage?.formatName) ? (
                    <Shuffle />
                  ) : (
                    <Group />
                  )}
                </span>
                {stage?.formatName} Stage
              </span>
            }
            subtitle={stage?.tournamentName}
          />
          {stage && isLiveRoundFormat(stage.formatName) ? (
            // Mexicano/Roulette: setup only, no generated schedule (spec 2026-09-29 §5).
            <div className="create-schedule-body">
              <div className="step-container">
                <MexicanoSetupStep
                  stage={stage}
                  onSaved={() =>
                    navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}/stages/${stageId}`, { replace: true })
                  }
                />
              </div>
            </div>
          ) : (
          <>
          <div className="create-schedule-body">
            <div key={id} className="step-container">
              {currentStep === "dependent-stages" ? (
                <DependentStagesStep
                  stage={schedule.stage}
                  stageDependent={schedule.stageDependent}
                  setStageDependent={schedule.setStageDependent}
                  selectedStages={schedule.selectedStages}
                  setSelectedStages={schedule.setSelectedStages}
                  selectedTeams={schedule.selectedTeams}
                  setSelectedTeams={schedule.setSelectedTeams}
                />
              ) : currentStep === "qualification-rules" ? (
                <QualificationRulesStep
                  stage={schedule.stage}
                  stageDependent={schedule.stageDependent}
                  activeStageTab={schedule.activeStageTab}
                  setActiveStageTab={schedule.setActiveStageTab}
                  selectedStages={schedule.selectedStages}
                  setSelectedStages={schedule.setSelectedStages}
                  createWithEmptyTeams={schedule.createWithEmptyTeams}
                  setCreateWithEmptyTeams={schedule.setCreateWithEmptyTeams}
                  rankedGroupTeams={schedule.rankedGroupTeams}
                  rankedTeamsLoading={schedule.rankedTeamsLoading}
                  fetchAndRankGroupTeams={schedule.fetchAndRankGroupTeams}
                />
              ) : currentStep === "scheduling-type" ? (
                <SchedulingTypeStep
                  stage={schedule.stage}
                  selectedType={schedule.selectedType}
                  setSelectedType={schedule.setSelectedType}
                  numOfGroups={schedule.numOfGroups}
                  setNumOfGroups={schedule.setNumOfGroups}
                  selectedTeams={schedule.selectedTeams}
                  stageDependent={schedule.stageDependent}
                />
              ) : currentStep === "manage-groups" ? (
                <ManageGroupsStep
                  stage={schedule.stage}
                  selectedTeams={schedule.selectedTeams}
                  numOfGroups={schedule.numOfGroups}
                  setNumOfGroups={schedule.setNumOfGroups}
                  groups={schedule.groups}
                  setGroups={schedule.setGroups}
                  getTeamGroup={schedule.getTeamGroup}
                  handleGroupChange={schedule.handleGroupChange}
                />
              ) : currentStep === "manage-bracket" ? (
                <ManageBracketStep
                  selectedTeams={schedule.selectedTeams}
                  bracketMatches={schedule.bracketMatches}
                  previewLoading={schedule.previewLoading}
                  activeRound={schedule.activeRound}
                  setActiveRound={schedule.setActiveRound}
                  handleMatchChange={schedule.handleMatchChange}
                  getSelectableTeams={schedule.getSelectableTeams}
                  getRoundAbbr={schedule.getRoundAbbr}
                  isRandomMatches={schedule.isRandomMatches}
                  setIsRandomMatches={schedule.setIsRandomMatches}
                />
              ) : currentStep === "match-frequency" ? (
                <MatchFrequencyStep
                  stage={schedule.stage}
                  matches={schedule.matches}
                  groups={schedule.groups}
                  bracketMatches={schedule.bracketMatches}
                  createWithEmptyTeams={schedule.createWithEmptyTeams}
                  selectedDates={schedule.selectedDates}
                  matchConfigs={schedule.matchConfigs}
                  groupDateAssignments={schedule.groupDateAssignments}
                  groupVenueAssignments={schedule.groupVenueAssignments}
                  knockoutVenueAssignments={schedule.knockoutVenueAssignments}
                  leagueVenueAssignments={schedule.leagueVenueAssignments}
                  venueRange={schedule.venueRange}
                  setVenueRange={schedule.setVenueRange}
                  addDate={schedule.addDate}
                  removeDate={schedule.removeDate}
                  updateConfig={schedule.updateConfig}
                  addGroupDate={schedule.addGroupDate}
                  removeGroupDate={schedule.removeGroupDate}
                  updateGroupVenueAssignment={schedule.updateGroupVenueAssignment}
                  autoAssignVenuesToGroups={schedule.autoAssignVenuesToGroups}
                  updateKnockoutVenueAssignment={schedule.updateKnockoutVenueAssignment}
                  autoAssignVenuesToKnockoutRounds={schedule.autoAssignVenuesToKnockoutRounds}
                  updateLeagueVenueAssignment={schedule.updateLeagueVenueAssignment}
                  autoAssignVenuesToLeague={schedule.autoAssignVenuesToLeague}
                  calculateTotalVenuesNeeded={schedule.calculateTotalVenuesNeeded}
                  nextHandler={schedule.nextHandler}
                  americanoUnitCount={schedule.americanoUnitCount}
                  americanoCourts={schedule.americanoCourts}
                  americanoRounds={schedule.americanoRounds}
                  updateAmericanoCourts={schedule.updateAmericanoCourts}
                  updateAmericanoRounds={schedule.updateAmericanoRounds}
                />
              ) : currentStep === "match-scheduling" ? (
                <MatchSchedulingStep
                  stage={schedule.stage}
                  matches={schedule.matches}
                  previewLoading={schedule.previewLoading}
                  activeSchedulingRound={schedule.activeSchedulingRound}
                  setActiveSchedulingRound={schedule.setActiveSchedulingRound}
                  matchChangeHandler={schedule.matchChangeHandler}
                  getDependentMatch={schedule.getDependentMatch}
                />
              ) : null}
            </div>
          </div>
          <div className="create-schedule-footer">
            <Button variant="ghost" className="footer-btn back" onClick={backHandler} disabled={createLoading || isFirstStep}>
              Back
            </Button>
            <Button className="footer-btn next" onClick={() => nextHandler(false)} disabled={createLoading}>
              {currentStep === "match-scheduling" ? (createLoading ? "Creating..." : "Create schedule") : "Next"}
            </Button>
          </div>
          </>
          )}
        </>
      )}
      {createLoading && (
        <div className="create-schedule-overlay-loader">
          <div className="create-schedule-overlay-panel">
            <Spinner size="lg" label="Creating schedule..." />
            <span className="loading-text">Creating schedule...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateSchedule;
